// ============================================================================
// ObjectCard.tsx — УНИВЕРСАЛЬНАЯ карточка строки объекта (карточный движок,
// план 11): единый рендерер, применяется ВЕЗДЕ — списки (viewMode=cards),
// формы (элемент «Карточка»), страницы (лента карточек), задачи, публичные
// страницы. Раскладка по ВАЖНОСТИ поля (importance 1..5, default 3):
//   5 — заголовок карточки (+ бейдж статуса со стилем)
//   4 — вторая строка (ключевые показатели)
//   3 — основная строка (обычные поля)
//   1–2 — страницы-точки (листание) либо скрыты
// Пустые/системные поля пропускаются; формат значения по типу (число/дата/
// булево/ссылка-presentation/перечисление-синоним/файл-превью).
// ============================================================================

import { useMemo, useState } from 'react';
import { FileText, Image as ImageIcon, ListChecks, CheckCircle2, Star } from 'lucide-react';
import { formatValue as formatValueUnified } from '@/lib/format';

export interface CardField {
  name: string;           // физическая колонка
  synonym?: string;       // отображаемое имя
  dataType?: string;      // text/number/date/boolean/reference/enumeration/file/...
  importance?: number;    // 1..5 (default 3)
  refTable?: string;      // для ссылок (presentation из {name}_presentation)
  style?: string;         // имя стиля (для перечислений/статусов)
  format?: string;        // формат вывода (SSoT — metadata_fields.format)
}

// Шаблон карточки (CardTemplate): именованный профиль отображения —
// variant/behavior/блоки. SSoT — сервер (metadata_card_templates).
export interface CardTemplateDef {
  name?: string;
  variant?: 'card' | 'plain' | 'compact';
  behavior?: {
    onClick?: 'whole' | 'buttons' | 'both';
    swipe?: 'off' | 'pages' | 'hide';
    animation?: 'system' | 'subtle' | 'none';
  };
  blocks?: Array<{
    type: 'fields' | 'image' | 'actions' | 'cards';
    minImportance?: number; // для fields: показывать поля важности >= N
    max?: number;           // для fields: максимум полей
    title?: string;
  }>;
}

export interface CardAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'outline' | 'danger';
}

export interface ObjectCardProps {
  fields: CardField[];
  data: Record<string, unknown>;
  // Заполненность (quality.overall, SSoT-сервер): full|partial|none —
  // круглый маркер в правом верхнем углу карточки
  qualityMark?: string;
  // Представления ссылок (серверные {col}_presentation)
  presentations?: Record<string, string>;
  // Синонимы значений перечислений: {col: {value: synonym}}
  enumSynonyms?: Record<string, Record<string, string>>;
  // Стили: {имяСтиля: attributes} — для бейджей (статус/перечисление)
  styles?: Record<string, Record<string, unknown>>;
  // Заголовок карточки (если важность 5 не задана — первое поле)
  title?: string;
  onClick?: () => void;
  // Мульти-выбор (long-press): обработчики нажатий/отпускания (мышь + тач)
  onMouseDown?: () => void;
  onTouchStart?: () => void;
  onMouseUp?: () => void;
  onTouchEnd?: () => void;
  onMouseLeave?: () => void;
  variant?: 'card' | 'plain' | 'compact' | 'tile';
  className?: string;
  // Блок ACTIONS: кнопки действий (переходы статусов, Взять, Купить, Скрыть...)
  actions?: CardAction[];
  // Блок IMAGE: превью файла-изображения (dataURL из кэша файлов; если нет — иконка)
  filePreview?: (fileId: string) => string | undefined;
  // Блок CARDS: ВЛОЖЕННЫЕ карточки (подчинённые/ТЧ/предмет) — рекурсия ≤ 3
  nested?: Array<{ title?: string; card: Omit<ObjectCardProps, 'nested'> }>;
  // Шаблон карточки (CardTemplate): variant/behavior/blocks — применяется ПОВЕРХ
  // авто-раскладки по важности (поля с minImportance/лимитами)
  template?: CardTemplateDef;
  // Вид «Плитка» (variant='tile'): иконка + подпись + бейджи по центру —
  // единый вид для папок задач и элементов разделов (как 1С)
  icon?: React.ComponentType<{ className?: string }>;
  badges?: React.ReactNode;
  // Непрочитанные в обсуждении объекта (контекстный чат) — бейдж
  unreadCount?: number;
  // Теги записи (чипы внизу карточки; серверные связи — батч из списка)
  tags?: Array<{ id: string; name: string }>;
}

// Число из значения метрики (может прийти строкой) — иначе null (бейдж не рендерим).
function metricNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function styleCss(attrs: Record<string, unknown> | undefined): React.CSSProperties {
  if (!attrs) return {};
  const css: React.CSSProperties = {};
  const tc = String(attrs.textColor || '');
  const bc = String(attrs.backgroundColor || '');
  if (tc) css.color = tc.startsWith('#') ? tc : `var(--color-${tc})`;
  if (bc) css.backgroundColor = bc.startsWith('#') ? bc : `var(--color-${bc})`;
  if (attrs.bold) css.fontWeight = 600;
  if (attrs.italic) css.fontStyle = 'italic';
  if (attrs.strikethrough) css.textDecoration = 'line-through';
  if (attrs.underline) css.textDecoration = 'underline';
  return css;
}

function formatValue(field: CardField, value: unknown, presentations: Record<string, string> | undefined, enumSynonyms: Record<string, Record<string, string>> | undefined, styles: Record<string, Record<string, unknown>> | undefined): { text: string; style?: React.CSSProperties; isFile?: boolean; isImage?: boolean } {
  if (value === null || value === undefined || value === '') return { text: '' };
  const dt = String(field.dataType || '').toLowerCase();
  const v = String(value);

  // Ссылка: представление с сервера
  if (dt === 'reference' || dt === 'uuid' || dt === 'anyref') {
    const pres = presentations?.[field.name + '_presentation'];
    if (pres) return { text: pres };
    return { text: v.slice(0, 8) === '' ? v : v };
  }
  // Перечисление: синоним + стиль
  if (dt === 'enumeration') {
    const syn = enumSynonyms?.[field.name]?.[v] || v;
    const st = styles?.[field.style || ''];
    if (st) return { text: syn, style: styleCss(st) };
    return { text: syn };
  }
  // Булево
  if (dt === 'boolean') return { text: v === '1' || v === 'true' ? '✓' : '—' };
  // Файл
  if (dt === 'file') {
    const isImage = /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(v);
    return { text: v, isFile: true, isImage };
  }
  // Дата/время/число — ЕДИНЫЙ механизм форматирования (SSoT — формат поля,
  // часовой пояс конфигурации): 'date:dd.MM.yyyy', 'number:2', ...
  if (dt === 'date' || dt === 'timestamp' || dt === 'datetime' || dt === 'number') {
    return { text: formatValueUnified(value, field.format, dt) };
  }
  return { text: v };
}

// Раскладка по важности
function layout(fields: CardField[]) {
  const title: CardField[] = [];
  const row4: CardField[] = [];
  const row3: CardField[] = [];
  const pages: CardField[][] = [];
  let extra: CardField[] = [];
  for (const f of fields) {
    const imp = Number(f.importance) || 3;
    if (imp >= 5) title.push(f);
    else if (imp === 4) row4.push(f);
    else if (imp === 3) row3.push(f);
    else extra.push(f);
  }
  if (extra.length > 0) {
    const pageSize = Math.max(3, Math.ceil(extra.length / 2));
    for (let i = 0; i < extra.length; i += pageSize) pages.push(extra.slice(i, i + pageSize));
  }
  return { title, row4, row3, pages };
}

export function ObjectCard({ fields, data, presentations, enumSynonyms, styles, onClick, onMouseDown, onTouchStart, onMouseUp, onTouchEnd, onMouseLeave, variant = 'card', className, actions, filePreview, nested, template, icon, badges, title, unreadCount = 0, qualityMark, tags }: ObjectCardProps) {
  // Маркер заполненности (правый верхний угол): зелёный/янтарный/красный
  const qualityDot = qualityMark && qualityMark !== 'full' ? (
    <span
      title={qualityMark === 'none' ? 'Не заполнено' : 'Заполнено частично'}
      className={`absolute top-2 right-2 w-2.5 h-2.5 rounded-full z-10 ${
        qualityMark === 'none' ? 'bg-destructive' : 'bg-amber-500'}`}
    />
  ) : null;
  // Вид «Плитка»: иконка + подпись + бейджи (папки задач, элементы разделов).
  // ЕДИНЫЙ вид стандартного ObjectCard — никаких отдельных виджетов плиток.
  if (variant === 'tile') {
    const TileIcon = icon;
    const marked = data.is_marked === true || data.is_marked === 'true' || data.is_marked === '1';
    return (
      <button
        type="button"
        onClick={onClick}
        className={`flex flex-col items-center justify-center gap-2 p-4 rounded-lg border bg-card text-card-foreground shadow-sm transition-colors hover:bg-accent/40 hover:shadow-md cursor-pointer ${marked ? 'opacity-70' : ''} ${className || ''}`}
      >
        {TileIcon && <TileIcon className="h-6 w-6 text-muted-foreground" />}
        <span className="text-sm font-semibold text-center">{title || ''}</span>
        {badges}
      </button>
    );
  }

  const [page, setPage] = useState(0);
  // Шаблон: variant из шаблона; blocks-fields фильтруют поля по важности/лимиту
  const effectiveVariant = template?.variant || variant;
  const fieldsBlock = template?.blocks?.find((b) => b.type === 'fields');
  const effFields = fieldsBlock
    ? fields.filter((f) => (Number(f.importance) || 3) >= (fieldsBlock.minImportance || 1)).slice(0, fieldsBlock.max || fields.length)
    : fields;
  const { title: layoutTitle, row4, row3, pages } = useMemo(() => layout(effFields), [effFields]);
  // Метрики строки (серверные колонки metric_<код>; для заявок — tasks_total/
  // tasks_done). Приходят строкой или числом; нет метрик — ничего не рендерим.
  const tasksTotal = metricNumber(data.metric_tasks_total);
  const tasksDone = metricNumber(data.metric_tasks_done);
  const hasTaskMetrics = tasksTotal !== null || tasksDone !== null;
  // Рейтинг объекта (мастера): сервер кладёт rating_avg/reviews_count (а при
  // настроенной метрике — ещё и metric_rating/metric_reviews_count).
  const ratingAvg = metricNumber(data.rating_avg) ?? metricNumber(data.metric_rating);
  const reviewsCount = metricNumber(data.reviews_count) ?? metricNumber(data.metric_reviews_count);
  const hasRating = ratingAvg !== null && reviewsCount !== null && reviewsCount > 0;

  const renderCell = (f: CardField) => {
    const value = data[f.name];
    const fmt = formatValue(f, value, presentations, enumSynonyms, styles);
    if (!fmt.text) return null;
    // Блок IMAGE: файл-изображение с важностью 4–5 — превью-миниатюра
    const isHighFile = fmt.isImage && (Number(f.importance) || 3) >= 4;
    const preview = isHighFile ? filePreview?.(fmt.text) : undefined;
    return (
      <span key={f.name} className="inline-flex items-center gap-1 min-w-0" title={f.synonym || f.name}>
        {isHighFile ? (
          preview ? (
            <img src={preview} alt={f.synonym || f.name} className="h-10 w-10 object-cover rounded shrink-0 border" />
          ) : (
            <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
          )
        ) : fmt.isImage ? <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" /> : fmt.isFile ? <FileText className="h-3 w-3 shrink-0 text-muted-foreground" /> : null}
        <span className="truncate" style={fmt.style}>{fmt.text}</span>
      </span>
    );
  };

  const behaviorOnClick = template?.behavior?.onClick;
  const clickable = behaviorOnClick === 'buttons' ? false : behaviorOnClick === 'both' ? true : onClick !== undefined;
  // Внешний вид (по требованию UX): минимум тени и скругления, деликатный hover
  const marked = data.is_marked === true || data.is_marked === 'true' || data.is_marked === '1';
  const wrap = effectiveVariant === 'compact' ? '' : `relative border rounded-md p-3 bg-background shadow-none transition-colors ${clickable ? 'hover:bg-accent/40 cursor-pointer' : ''} ${marked ? 'opacity-70' : ''}`;
  const currentPage = pages[Math.min(page, Math.max(0, pages.length - 1))] || [];

  return (
    <div
      data-object-card={effectiveVariant === 'compact' ? undefined : 'true'}
      className={`${wrap} ${className || ''}`}
      onClick={clickable ? onClick : undefined}
      onMouseDown={onMouseDown}
      onTouchStart={onTouchStart}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
      onMouseLeave={onMouseLeave}
    >
      {/* Пометка на удаление: маленький красный ✕ в углу */}
      {marked && (
        <span
          className="absolute top-1 right-1 h-4 w-4 flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold"
          title="Помечен на удаление"
        >
          ✕
        </span>
      )}
      {/* Проведён: зелёная галочка в правом нижнем углу ПОВЕРХ карточки (не занимает строку) */}
      {(data.is_posted === true || data.is_posted === 1 || String(data.is_posted).toLowerCase() === 'true' || String(data.is_posted) === '1') && (
        <span
          className="absolute bottom-1 right-1 text-green-600 font-bold text-xs leading-none pointer-events-none"
          title="Проведён"
        >
          ✓
        </span>
      )}
      {/* Заполненность: маркер в правом верхнем углу */}
      {qualityDot}
      {/* Непрочитанные в обсуждении (контекстный чат): бейдж в правом нижнем углу */}
      {unreadCount > 0 && (
        <span
          className="absolute bottom-1 right-1 text-[9px] leading-none bg-primary text-primary-foreground rounded-full px-1.5 py-1"
          title="Непрочитанные в обсуждении"
        >
          {unreadCount}
        </span>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate flex items-center gap-1.5">
            {layoutTitle.map(renderCell)}
            {layoutTitle.length === 0 && (data.name ? String(data.name) : data.code ? String(data.code) : '')}
          </div>
          {row4.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-foreground/90 mt-1 flex-wrap">
              {row4.map(renderCell)}
            </div>
          )}
          {row3.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
              {row3.map(renderCell)}
            </div>
          )}
          {pages.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap">
              {currentPage.map(renderCell)}
            </div>
          )}
          {/* Бейджи метрик (заявки: задачи по заявке) — компактно, с иконками */}
          {hasRating && (
            <div className="flex items-center gap-1 mt-1 text-[11px] text-amber-700" title="Средняя оценка и количество отзывов">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
              <span className="font-medium tabular-nums">{ratingAvg!.toFixed(1)}</span>
              <span className="text-muted-foreground">· {reviewsCount} отз.</span>
            </div>
          )}
          {hasTaskMetrics && (
            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
              {tasksTotal !== null && (
                <span className="inline-flex items-center gap-1" title="Всего задач">
                  <ListChecks className="h-3 w-3" />
                  Задач: {tasksTotal}
                </span>
              )}
              {tasksDone !== null && (
                <span className="inline-flex items-center gap-1" title="Выполнено задач">
                  <CheckCircle2 className="h-3 w-3" />
                  Выполнено: {tasksDone}
                </span>
              )}
            </div>
          )}
          {tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {tags.map((t) => (
                <span
                  key={t.id}
                  title={t.name}
                  className="inline-flex items-center rounded-full border border-border/60 bg-muted/40 px-1.5 py-0.5 text-[10px] leading-none text-foreground/80 max-w-[140px]"
                >
                  <span className="truncate">{t.name}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {pages.length > 1 && (
        <div className="flex items-center justify-center gap-1 mt-1.5">
          {pages.map((_, i) => (
            <button
              key={i}
              className={`h-1.5 rounded-full ${i === Math.min(page, pages.length - 1) ? 'w-3 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
              onClick={(e) => { e.stopPropagation(); setPage(i); }}
            />
          ))}
        </div>
      )}
      {/* Блок ACTIONS: кнопки действий (переходы статусов, Взять, Купить, Скрыть...) */}
      {actions && actions.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-muted/30" onClick={(e) => e.stopPropagation()}>
          {actions.map((a, i) => (
            <button
              key={i}
              className={`h-6 px-2 text-[11px] rounded border ${
                a.variant === 'primary' ? 'bg-primary text-primary-foreground border-primary'
                : a.variant === 'danger' ? 'text-destructive border-destructive/40 hover:bg-destructive/10'
                : 'hover:bg-accent'
              }`}
              onClick={a.onClick}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
      {/* Блок CARDS: ВЛОЖЕННЫЕ карточки (подчинённые/ТЧ/предмет) — рекурсия ≤ 3 */}
      {nested && nested.length > 0 && (
        <div className="mt-2 space-y-1.5 border-t border-muted/30 pt-2">
          {nested.map((n, i) => (
            <div key={i} className="rounded border border-muted/40 p-2">
              {n.title && <div className="text-[10px] font-medium text-muted-foreground mb-1">{n.title}</div>}
              <ObjectCard {...n.card} variant="plain" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
